/**
 * Typed contract for `POST /api/report/GenerateChartReport` — the unified
 * replacement for `Report/BarChartNew` + `Report/SpiderChartNew`.
 *
 * Both legacy endpoints returned the same 43-property `ImpactLineViewModel`
 * with values packed into parallel `;`-joined strings that had to be split and
 * re-aligned positionally. The new endpoint returns one calculation-engine run
 * where every number carries its own scenario / indicator / bucket, so nothing
 * here needs `.split(';')`.
 *
 * Sections that were not requested are omitted from the JSON entirely (they are
 * not present as `null`), hence every section property below is optional —
 * always guard on presence before binding.
 */

/** Bit flags for the optional `sections` request field. */
export enum ChartReportSections {
  /** Radar chart, 0-1 normalized across the compared scenarios. */
  Spider = 1,
  /** Stacked contribution chart + table, per indicator. */
  Contributions = 2,
  /** The 21-row packaging gauge. */
  PackagingMetrics = 4,
  /** Per scenario x indicator raw total, saving %, normalized value. */
  ScenarioTotals = 8,
  /** PEF normalized + weighted per-indicator breakdowns. */
  Pef = 16,
  All = 31,
}

/**
 * What the "chart at a glance" screen needs for each chart type. The gauge row
 * and the scenario totals are rendered regardless of which chart is on screen,
 * so they are always requested.
 */
export const SPIDER_SECTIONS =
  ChartReportSections.Spider |
  ChartReportSections.PackagingMetrics |
  ChartReportSections.ScenarioTotals;

export const BAR_SECTIONS =
  ChartReportSections.Contributions |
  ChartReportSections.PackagingMetrics |
  ChartReportSections.ScenarioTotals;

/**
 * The PEF-categories view needs the `pef` section (which carries both the
 * normalized and the weighted breakdown) plus the gauge. It does not need
 * `scenarioTotals`: that section's PEF entry is the aggregate single score,
 * which is the *sum* of `pef.weighted` across indicators, not the breakdown.
 */
export const PEF_SECTIONS = ChartReportSections.Pef | ChartReportSections.PackagingMetrics;

/**
 * `ChartReportRequest` — the new endpoint's own model, NOT `GetChartViewModel`.
 * An old BarChartNew / SpiderChartNew body will not bind. Every id comes
 * straight off `GetChartReportFormData`, with no translation.
 *
 * The legacy triplet `indicatorIds` / `indicatorIdd` / `indicatorIddStr` — each
 * honoured under a different condition — collapses to one field and one rule:
 * a non-empty `indicatorIds` means "use exactly these", otherwise the set is
 * derived from `impactCategoryId`.
 */
export interface GenerateChartReportRequest {
  projectId: number;
  /** csv of `scenarios[].idStr`. */
  scenarioIds: string;
  baseScenarioId: number;
  /** `chartTypes[].id` — 2 bar, 3 spider. */
  chartTypeId: number;
  /** `chartTypes[].comparisons[].id`, already a `ReportTypeEnum` value (bar). */
  reportTypeId: number;
  /** `chartTypes[].impactCategories[].id` (spider). */
  impactCategoryId: number;
  /** csv of `indicators[].id`. Empty = derive the set from `impactCategoryId`. */
  indicatorIds: string;
  functionalUnitId: number;
  isExcludeProductImpacts: boolean;
  isExport: boolean;
  /** Bit flags, see {@link ChartReportSections}. Omit to infer from `chartTypeId`. */
  sections?: number;
}

export interface ChartReportMeta {
  sections: number;
  functionalUnitId: number;
  functionalUnitName: string;
  chartTypeId: number;
  reportTypeId: number;
  contributionDimension: string;
  baseScenarioId: number;
  baseScenarioName: string;
  opacity: number;
  isExport: boolean;
  impactCategoryId: number;
}

export interface ChartReportScenario {
  scenarioId: number;
  name: string;
  shortName: string;
  numberOfUnits: number;
  isBaseScenario: boolean;
}

export interface ChartReportIndicator {
  id: number;
  name: string;
  unit: string;
  spiderIconUrl: string;
  tableIconUrl: string;
  equivalencyText: string;
  equivalencyImageUrl: string;
  multiplier: number;
  normalization: number;
}

/** Same `ChartOutput` / `ReportBuilderTableOutput` types `GenerateCustomReport` returns. */
export interface ChartReportChartOutput {
  type?: string;
  xAxis?: { categories?: string[]; categoryImages?: string[] };
  /** `label` is the full axis caption (indicator name + unit), not just the unit. */
  yAxis?: { min?: number; max?: number; label?: string; title?: string };
  series?: Array<{ name?: string; data?: Array<{ y?: number; percentage?: number }> }>;
  [key: string]: any;
}

export interface ChartReportSpider {
  functionalUnitName: string;
  scenarioCount: number;
  chart: ChartReportChartOutput;
}

/** One row of a stacked contribution chart: a bucket's value for one scenario. */
export interface ChartReportBucket {
  bucketKey: string;
  bucketName: string;
  scenarioId: number;
  value: number;
  percentage: number;
}

export interface ChartReportContribution {
  indicatorId: number;
  indicatorName: string;
  indicatorUnitName: string;
  table?: any;
  chart?: ChartReportChartOutput;
  buckets: ChartReportBucket[];
}

/** One gauge cell: metric `gaugeRow` (1-21) measured for one scenario. */
export interface ChartReportPackagingMetricRow {
  gaugeRow: number;
  metricId: number;
  label: string;
  scenarioId: number;
  value: number;
  formattedValue: string;
  isPercentage: boolean;
}

export interface ChartReportPackagingMetrics {
  table?: any;
  rows: ChartReportPackagingMetricRow[];
}

export interface ChartReportScenarioTotal {
  scenarioId: number;
  indicatorId: number;
  total: number;
  formattedTotal: string;
  savingPercentage: number;
  formattedSavingPercentage: string;
  /** 0-1 across the compared scenarios — this is the radar axis value. */
  normalizedValue: number;
}

/** One PEF cell: a scenario's value for one PEF category. */
export interface ChartReportPefValue {
  scenarioId: number;
  scenarioName: string;
  indicatorId: number;
  indicatorName: string;
  value: number;
  formattedValue: string;
  /** Share of that scenario's summed PEF value carried by this category. */
  percentage: number;
  formattedPercentage: string;
}

export interface ChartReportPef {
  weightingIndicatorId: number;
  /** Raw total x normalization. Legacy `pef_normalized`. */
  normalized: ChartReportPefValue[];
  /** Normalized x weighting / toxicity multiplier. Legacy `pef_weighted`. */
  weighted: ChartReportPefValue[];
  normalizedChart?: ChartReportChartOutput;
  weightedChart?: ChartReportChartOutput;
}

export interface ChartReportLookupItem {
  id: number;
  name: string;
  selected?: boolean;
  [key: string]: any;
}

/** `data` payload of the `ResponseDetail<T>` envelope. */
export interface GenerateChartReportData {
  meta: ChartReportMeta;
  scenarios: ChartReportScenario[];
  indicators: ChartReportIndicator[];
  spider?: ChartReportSpider;
  contributions?: ChartReportContribution[];
  packagingMetrics?: ChartReportPackagingMetrics;
  scenarioTotals?: ChartReportScenarioTotal[];
  pef?: ChartReportPef;
  speedosImpactCategoryItems?: ChartReportLookupItem[];
  orgSpeedosImpactCategoryItems?: ChartReportLookupItem[];
}
